import type {
	LocationResult,
	CoworkingSolution,
	FloorResult,
	ServiceOfficeMembershipResult,
} from '@alexanderathoodly/data-models'
import { useMemo, useState, useCallback } from 'react'
import { useQuery } from 'react-query'
import type { UseQueryResult } from 'react-query'
import { useUser } from 'reactfire'
import { getFloorsQuery } from '~/queries/coworking_location'
import {
	getAuthCoworkingSolutionsByLocationQuery,
	getCoworkingSolutionsByLocationQuery,
	getAICoworkingSolutionsByLocationQuery,
} from '~/queries/coworking_solution'
import { useAuthQuery } from '~/queries/useauthquery'
import { useSearchParams } from '~/utils/routing'
import { useLocationPage } from './context'
import {
	getRoomQuery,
	getAuthMembershipsQuery,
	getMembershipsQuery,
	getAIMembershipsQuery,
} from './queries'
import { useIsAIUserAgent } from '~/utils/ai'

export type CoworkingSolutionsParams = {
	workspaces: number | undefined
	budget: number | undefined
	type: string | undefined
}

export function useCoworkingSolutionsParams(): CoworkingSolutionsParams {
	const params = useSearchParams()
	const workspaces = params.get('min_ws')
	const budget = params.get('budget')
	const type = params.get('type')
	return {
		workspaces: workspaces ? Number(workspaces) : undefined,
		budget: budget ? Number(budget) : undefined,
		type: type ? type : undefined,
	}
}

export interface TabsData {
	label: string
	minValue: number
	maxValue: number
	solutions: CoworkingSolution[]
}

// If the user has filtered on workspaces, the solutions tab with a matching ws span should
// be selected otherwise the first tab that has solutions.
function findValidTab(
	tabsData: TabsData[],
	workspacesParam: number | undefined
): number {
	return tabsData.findIndex(
		(tab) =>
			tab.solutions.length > 0 &&
			(!workspacesParam || workspacesParam <= tab.maxValue)
	)
}

interface UseSolutionDisplay {
	location: LocationResult | undefined
	params: CoworkingSolutionsParams
	error: Error | null
	isError: boolean
	isLoading: boolean
	isIdle: boolean
	firstValidTabIndex: number
	selectedSolution: CoworkingSolution | undefined
	tabsData: TabsData[] | undefined
	selectSolution: (solution: CoworkingSolution | undefined) => void
	cheapestSolution: string | undefined
	smallestSolutionSize: number | undefined
	largestSolutionSize: number | undefined
}

export function useSolutionDisplay(): UseSolutionDisplay {
	const { location } = useLocationPage()
	const params = useCoworkingSolutionsParams()
	const { data: user } = useUser()
	const isAI = useIsAIUserAgent()
	const resultWithoutAuth = useQuery(
		getCoworkingSolutionsByLocationQuery(location?.id)
	)
	const resultWithAuth = useAuthQuery(
		getAuthCoworkingSolutionsByLocationQuery,
		location?.id
	)
	const resultWithAI = useQuery({
		...getAICoworkingSolutionsByLocationQuery(location?.id),
		enabled: isAI && !!location?.id,
	})
	const result = isAI ? resultWithAI : user ? resultWithAuth : resultWithoutAuth

	const tabsData = useMemo(() => {
		if (result.data) {
			if (result.data.sorted_solutions.length == 0) {
				return []
			}
			const tabsData: TabsData[] = result.data.sorted_solutions.map(
				(solutionsGroup, index) => {
					return {
						label:
							index == result.data.sorted_solutions.length - 1
								? `${solutionsGroup.min_value}+`
								: `${solutionsGroup.min_value}-${solutionsGroup.max_value}`,
						minValue: solutionsGroup.min_value,
						maxValue: solutionsGroup.max_value,
						solutions: solutionsGroup.solutions,
					}
				}
			)
			return tabsData
		}
		return undefined
	}, [result.data])

	const firstValidTabIndex = useMemo(
		() => (tabsData ? findValidTab(tabsData, params.workspaces) : -1),
		[tabsData, params.workspaces]
	)

	const [selectedSolution, setSelectedSolution] = useState<
		CoworkingSolution | undefined
	>()

	const selectSolution = useCallback(
		(solution: CoworkingSolution | undefined) => setSelectedSolution(solution),
		[]
	)

	return {
		location,
		params,
		error: result.error,
		isError: result.isError,
		isLoading: result.isLoading,
		isIdle: result.isIdle,
		selectedSolution,
		firstValidTabIndex,
		tabsData,
		selectSolution,
		cheapestSolution:
			result.data &&
			(Math.round(result.data.min_solution_price / 100) * 100).toLocaleString(
				'sv-SE'
			),
		smallestSolutionSize: result.data?.min_workspaces,
		largestSolutionSize: result.data?.max_workspaces,
	}
}

export function useFloorsWithBounds(
	location: LocationResult | undefined
): FloorResult[] {
	const floorIds = location ? location.floors.map((floor) => floor.id) : []
	const floors = useQuery(getFloorsQuery(floorIds))
	const floorsWithBounds = useMemo(
		() => (floors.data || []).filter((floor) => floor.bounds),
		[floors.data]
	)
	return floorsWithBounds
}

export function useSolutionFloorId(roomId: string): string | undefined {
	const roomQuery = useQuery(getRoomQuery(roomId))

	return roomQuery.data?.floor || undefined
}

export function useMemberships(
	locationId: string | undefined
): UseQueryResult<ServiceOfficeMembershipResult[], Error> {
	const { data: user } = useUser()
	const isAI = useIsAIUserAgent()
	const membershipWithoutAuth = useQuery(getMembershipsQuery(locationId))
	const membershipsWithAuth = useAuthQuery(getAuthMembershipsQuery, locationId)
	const membershipWithAI = useQuery({
		...getAIMembershipsQuery(locationId),
		enabled: isAI && !!locationId,
	})

	const memberships = isAI
		? membershipWithAI
		: user
		? membershipsWithAuth
		: membershipWithoutAuth

	return memberships
}
